import {
  AnyMessage,
  MethodInfoUnary,
  PartialMessage,
  ServiceType,
} from "@bufbuild/protobuf";
import { createPromiseClient, Transport } from "@connectrpc/connect";

import Enum from "@repo/utils/Enum";
import Log from "@repo/utils/Log";
import UFunction from "@repo/utils/UFunction";
import UPromise from "@repo/utils/UPromise";
import UString from "@repo/utils/UString";
import UType from "@repo/utils/UType";
import Env from "@repo/utils-client/Env";

import { Account } from "../../gen/web/public/user_connect";
import Rpc, { RpcError } from "../utils/Rpc";
import Stub from "../utils/Stub";

import { kikoffApiServices, usServices } from "./services";

const stubs = import.meta.env.DEV
  ? UFunction.memo(() => import("../usa/stubs").then((res) => res.default))
  : undefined;

const serviceCache: Rpc.Cache = {};

export const usTransport = Rpc.createTransport({
  basePath: "/api/v1",
  serviceCache,
});

usTransport.events.on("beforeRequest", (context) => {
  Object.assign(context.options.headers, {
    "X-Kikoff-Traffic": Env.config.debug
      ? new URLSearchParams(window.location.search).get("CANARY_TRAFFIC")
      : undefined,
  });
});

// We always need this service so we'll include it immediately
serviceCache.Account = {
  spec: Account,
  client: createPromiseClient(Account, usTransport),
};

const usRpc = createStatusClient(
  {
    ...usServices,
    ...kikoffApiServices,
  },
  { stubs, transport: usTransport }
);
export default usRpc;

function createStatusClient<
  Definitions extends Record<
    string,
    () => Promise<Record<string, ServiceType>>
  >,
>(
  definitions: Definitions,
  {
    stubs,
    transport,
  }: {
    stubs?: () => Promise<
      Record<
        string,
        {
          enabled?: boolean;
          default: Stub.Schema<Record<string, () => Promise<any>>>;
        }
      >
    >;
    transport: Transport;
  }
) {
  return new Proxy(
    {},
    {
      get(_, serviceName: string) {
        return new Proxy(
          {},
          {
            get: (_, methodName: string) => (req: any, options: any) => {
              const handlerByStatus = {} as Record<
                string,
                (res: AnyMessage) => any
              >;
              const promise = (async () => {
                if (!serviceCache[serviceName]) {
                  const specKey = (definitions as any)[serviceName]
                    ? serviceName
                    : `${serviceName}Service`;
                  const spec = (await (definitions as any)[specKey]())[specKey];
                  serviceCache[serviceName] = {
                    spec,
                    client: createPromiseClient(spec, transport),
                  };
                }

                const { client, spec } = serviceCache[serviceName]!;
                let isStubbed = false;
                const withStubsIfAny = (async function next(
                  [current, ...rest],
                  req
                ): Promise<AnyMessage> {
                  if (!current)
                    return client[methodName]!(
                      req,
                      options
                    ) as Promise<AnyMessage>;

                  const stub =
                    current.enabled &&
                    current.default[
                      serviceName as keyof typeof current.default
                    ]?.[methodName];
                  if (!stub) return next(rest, req);

                  if (!isStubbed) {
                    isStubbed = true;
                    // Add default latency to make loading states visible
                    await UPromise.delay(500);
                  }

                  const res = await stub({
                    req,
                    makeRequest: (stubRequest = req) => next(rest, stubRequest),
                  });

                  const O = spec.methods[methodName]!.O as any;
                  res.status ??= res.error ? O.Status.FAILED : O.Status.SUCCESS;

                  return new O(res);
                })(Object.values((await stubs?.()) || ({} as never)), req);

                Log.withTrace`${Log.color("grey", "rpc")} ${Log.fn(
                  [Log.color("#faf", "usRpc"), serviceName, methodName],
                  [
                    new spec.methods[methodName]!.I(req).toJson({
                      enumAsInteger: false,
                    }),
                  ]
                )}: ${
                  isStubbed
                    ? Log.join(
                        [
                          Log.style("color:white;background:red", "[STUBBED]"),
                          " ",
                        ],
                        ""
                      )
                    : ""
                }${withStubsIfAny.then((res) =>
                  res.toJson({ enumAsInteger: false })
                )}`;

                return withStubsIfAny;
              })().then((res) => {
                const O: any =
                  serviceCache[serviceName]!.spec.methods[methodName]!.O;
                const handler = handlerByStatus[O.Status?.[res.status]];
                if (handler) return handler(res);
                return handlerByStatus.elseThrow?.(res) || res;
              });

              return (function next(path: string[] = []): any {
                return new Proxy(UFunction.noop, {
                  apply(_, __, args) {
                    for (const status of path) {
                      handlerByStatus[status] = args[0];
                    }
                    return next();
                  },
                  get(_, p: string) {
                    if (p in promise)
                      return (...args: any[]) => (promise as any)[p](...args);

                    if (p === "elseThrow")
                      return (defaultMessage: string) => {
                        handlerByStatus.elseThrow = (message) => {
                          throw new RpcError([
                            {
                              code: "NONE" as any,
                              message:
                                message.error?.description || defaultMessage,
                            },
                          ]);
                        };
                        return promise;
                      };

                    return next([...path, p]);
                  },
                });
              })();
            },
          }
        );
      },
    }
  ) as {
    [ServiceName in keyof Definitions as UString.BeforeSuffix<
      UType.Narrow<ServiceName, string>,
      "Service"
    >]: UType.Narrow<
      Awaited<ReturnType<Definitions[ServiceName]>>,
      Record<ServiceName, ServiceType>
    >[ServiceName]["methods"] extends infer Service extends
      ServiceType["methods"]
      ? {
          [MethodName in keyof Service]: Service[MethodName] extends MethodInfoUnary<
            infer I,
            infer O
          >
            ? (request: PartialMessage<I>) => Service[MethodName] extends {
                O: { _enums: { Status: Enum.Any } };
              }
                ? ServiceMethodResult<Service[MethodName]>
                : Promise<O>
            : never;
        }
      : never;
  };
}

type ServiceMethodResult<
  MethodInfo extends MethodInfoUnary<AnyMessage, AnyMessage>,
  ReturnValues extends [any, any] = [
    MethodInfo extends MethodInfoUnary<any, infer O> ? O : never,
    never,
  ],
  StatusMap extends Enum.Any = MethodInfo extends {
    O: { _enums: { Status: infer Status extends Enum.Any } };
  }
    ? Status
    : never,
> =
  MethodInfo extends MethodInfoUnary<any, infer O>
    ? Promise<ReturnValues[number]> & {
        [Key in keyof StatusMap]: <ReturnValue>(
          callback: (data: O & { status: StatusMap[Key] }) => ReturnValue
        ) => ServiceMethodResult<
          MethodInfo,
          [ReturnValues[0], ReturnValues[1] | ReturnValue],
          Omit<StatusMap, Key>
        >;
      } & {
        elseThrow(defaultMessage?: string): Promise<Awaited<ReturnValues[1]>>;
      }
    : never;
